import React from 'react';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import { ARCHIVED_PROJECT_TOOLTIP } from '../../scripts/permission-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
      flexShrink: 0,
      gap: theme.spacing(0.5),
      // CENTERLINE blue, filled rather than outlined, and a size up from the surrounding text:
      // this is the one thing on the page that says the project cannot be changed, so it has to
      // read as a deliberate mark rather than a decoration on the project name.
      color: theme.palette.primary.main,
    },
    icon: {
      fontSize: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '20px',
      textTransform: 'none',
      whiteSpace: 'nowrap',
      color: 'inherit',
      marginTop: 2,
    },
  }),
);

interface Props {
  /**
   * Show the word "Archived" beside the icon where there is room for it. Used where the lock is
   * standing in for the breadcrumbs rather than sitting inside them -- on its own, an icon with
   * no project name next to it is easy to miss.
   */
  withLabel?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The mark that says a project is archived, and so read-only. Deliberately one component used in
 * every place the state has to show -- the breadcrumbs, the project selector, the favorites list
 * -- so the same lock means the same thing wherever a user meets it, and the explanation is one
 * hover away in all of them.
 *
 * It draws the mark and nothing else -- deciding whether a project is archived is the caller's,
 * since each of those places reads that from a different source.
 */
export default function ArchivedProjectLock(props: Props) {
  const { withLabel, className, style } = props;
  const classes = useStyles();
  // The theme comes from the core `useTheme`, which falls back to the default theme: the
  // callback form of useMediaQuery reads a null theme outside a ThemeProvider, and the Outlook
  // add-in renders this lock without one.
  const theme = useTheme<Theme>();
  // Only worth the words on a screen wide enough not to crowd the toolbar.
  const hasRoomForLabel = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Tooltip arrow title={ARCHIVED_PROJECT_TOOLTIP}>
      <span
        className={`${classes.root} ${className ?? ''}`}
        style={style}
        data-testid="archived-project-lock"
      >
        <LockIcon className={classes.icon} />
        {withLabel && hasRoomForLabel && (
          <Typography component="span" className={classes.label}>
            Archived
          </Typography>
        )}
      </span>
    </Tooltip>
  );
}
